import { forwardRef, useCallback, useContext, useMemo } from "react";

import { cn } from "src/shadcn/lib/utils";
import { DropdownMenuItem as PrimitiveDropdownMenuItem } from "src/primitives/DropdownMenu";

import { DropdownContext, type MenuItemProps } from "./types";

const MenuItem = forwardRef<HTMLDivElement, MenuItemProps>(
  (
    {
      children,
      isActive = false,
      isDisabled = false,
      variant = "default",
      prefix,
      suffix,
      to,
      href,
      className,
      onClick,
      disabled: _disabled,
      asChild: _asChild,
      onSelect: _onSelect,
      ...otherProps
    },
    ref
  ) => {
    const { closeOnSelect } = useContext(DropdownContext);
    const linkHref = to ?? href;

    const handleSelect = useCallback(
      (e: Event) => {
        if (!closeOnSelect) e.preventDefault();
        onClick?.(e);
      },
      [closeOnSelect, onClick]
    );

    const itemContent = useMemo(
      () => (
        <>
          {prefix && (
            <span data-slot="menu-item-prefix" className="shrink-0">
              {prefix}
            </span>
          )}
          <span className="flex-1 truncate">{children}</span>
          {suffix && (
            <span
              data-slot="menu-item-suffix"
              className="ms-auto shrink-0 text-muted-foreground"
            >
              {suffix}
            </span>
          )}
        </>
      ),
      [prefix, children, suffix]
    );

    return (
      <PrimitiveDropdownMenuItem
        ref={ref}
        variant={variant}
        disabled={isDisabled}
        data-active={isActive || undefined}
        className={cn(
          isActive && "bg-accent text-accent-foreground",
          className
        )}
        asChild={!!linkHref}
        onSelect={handleSelect}
        {...otherProps}
      >
        {linkHref ? <a href={linkHref}>{itemContent}</a> : itemContent}
      </PrimitiveDropdownMenuItem>
    );
  }
);
MenuItem.displayName = "Dropdown.MenuItem";

export { MenuItem };
